import React, {
  useEffect, useState, useRef, Fragment,
} from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { FieldUpdateHandlerParam } from 'shared/form';
import { FormLayoutContent, FormLayoutFooter, FormLayout } from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import { RootState } from 'data/root.reducers';
import * as targetDomainSelectors from 'data/targetDomain/targetDomain.selectors';
import * as targetDomainActions from 'data/targetDomain/targetDomain.actions';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import * as securityInfoActions from 'data/securityInfo/securityInfo.actions';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import { TargetDomain } from 'data/targetDomain/targetDomain.types';
import TargetDomainForm from 'routes/TargetDomain/components/TargetDomainForm';
import * as types from 'data/targetDomain/targetDomain.types';
import { targetDomainTemplate } from '../../templates';
import {
  adaptTemplateVisibilityMode,
  adaptTemplateOptions,
  adaptFormData,
  adaptTargetDomainFormData,
  adaptTargetDomainTemplate,
} from '../../utils/targetDomainTemplate.adapter';
import { getAuditDetailsToSubmit } from '../../utils/targetDomainTemplateDataToSubmit';

type Ref = {
  isValid: (forceValidation: boolean, forceTouched: boolean) => boolean
  getValues: () => types.TargetDomain
}

type RouterParams = {
  history: string
}
const mapState = (state: RootState) => ({
  targetDomainTemplateDef: adaptTemplateVisibilityMode(targetDomainTemplate, 'edit'),
  deliveryFunctionId: state.global.selectedDF,
  selectedTargetDomain: targetDomainSelectors
    .getSelectedTargetDomain(state)[0],
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  originalSelectedTargetDomain: targetDomainSelectors
    .getSelectedTargetDomain(state)[0],
  targetDomain: targetDomainSelectors.getTargetDomain(state),
});

const mapDispatch = {
  getSecurityInfos: securityInfoActions.getSecurityInfos,
  goToTargetDomainList: targetDomainActions.goToTargetDomainList,
  updateTargetDomain: targetDomainActions.updateTargetDomain,
  getTargetDomain: targetDomainActions.getTargetDomain,
};
const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter;

export const TargetDomainEdit = ({
  history,
  selectedTargetDomain,
  originalSelectedTargetDomain,
  deliveryFunctionId,
  userId,
  userName,
  isAuditEnabled,
  targetDomainTemplateDef,
  targetDomain,
  goToTargetDomainList,
  updateTargetDomain,
  getTargetDomain,
}: Props): JSX.Element => {

  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<Partial<TargetDomain>>({});
  const [template, setTemplate] = useState(targetDomainTemplateDef);
  const { t } = useTranslation();

  const id = selectedTargetDomain?.id;

  useEffect(() => {
    if (id) {
      getTargetDomain(deliveryFunctionId, id);
    }
  }, [deliveryFunctionId, id, getTargetDomain]);

  useEffect(() => {
    if (targetDomain != null && template != null) {
      let clonedTargetDomain = cloneDeep(targetDomain);
      const clonedTemplate = cloneDeep(template);
      clonedTemplate.formId = Date.now().toString();

      clonedTargetDomain = adaptTargetDomainFormData(clonedTargetDomain);

      setTemplate(adaptTemplateOptions(clonedTemplate, targetDomain));
      setFormData(clonedTargetDomain);
    }
  }, [targetDomain, id]);

  const getUpdateTargetDomainActionPayload = () => {
    if (formRef.current === undefined || formRef.current === null) return null;

    if (!formRef.current.isValid(true, true)) {
      return null;
    }

    const targetDomainFormData = formRef.current.getValues();
    const payload = {
      data: targetDomainFormData,
      auditDetails: getAuditDetailsToSubmit(
        targetDomainFormData,
        originalSelectedTargetDomain,
        isAuditEnabled,
        userId,
        userName,
        'UPDATE',
      ),
    };

    return payload;

  };

  const handleClickSave = () => {
    const payload = getUpdateTargetDomainActionPayload();
    if (payload && targetDomain != null) {
      updateTargetDomain(deliveryFunctionId, targetDomain?.id, payload, history);
    }
  };

  const handleCancel = () => goToTargetDomainList(history);

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    if (template == null || formData == null) {
      return;
    }

    const clonedFormData = cloneDeep(formData as TargetDomain);
    let clonedTemplate = cloneDeep(template);

    let newData = {
      ...clonedFormData,
      [fieldName]: value,
    };

    clonedTemplate = adaptTargetDomainTemplate(clonedTemplate);

    newData = adaptFormData(newData);

    setFormData(newData);
    setTemplate(clonedTemplate);
  };

  const renderForm = () => {
    if (formData && template) {
      return (
        <TargetDomainForm
          ref={formRef}
          data={formData}
          template={template}
          isEditing
          onFieldUpdate={handleFieldUpdate}
        />
      );
    }
  };

  const renderFormPanel = (formTitle:string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing
          onClickCancel={handleCancel}
          onClickSave={handleClickSave}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  if (targetDomain == null) {
    return <Fragment />;
  }

  return (
    <div data-testid="TargetDomainEdit">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel(t('targetDomain.edit.title'))}
          {renderForm()}
        </FormLayoutContent>
        <FormLayoutFooter>
          {renderFormPanel('')}
        </FormLayoutFooter>
      </FormLayout>
    </div>
  );
};

export default withRouter(connector(TargetDomainEdit));
